import React, { useState, useEffect, useRef } from 'react';
import { exportCanvasImage } from './CanvasCardRenderer';
import CanvasCardRenderer from './CanvasCardRenderer';
import { themes } from '../themes';
import { trackShare, trackGameContent } from '../services/analytics';
import { useI18n } from '../hooks/useI18n';

/**
 * 简化的导出组件，专注于图片预览和快速导出功能
 */
const ExportSelector = ({
  actions,
  gameInfo,
  heroInfo,
  boardCards,
  isMobile,
  className = '',
  formattedText = '',
  gameState  // Add gameState prop to access it for sharing
}) => {
  const { t } = useI18n();
  const [isExporting, setIsExporting] = useState(false);
  const [clipboardSupported, setClipboardSupported] = useState(false);
  const [displayMode, setDisplayMode] = useState('image');
  const [copiedFeedback, setCopiedFeedback] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [linkCopiedFeedback, setLinkCopiedFeedback] = useState(false);
  const canvasRendererRef = useRef(null);
  
  // 检查设备是否支持clipboard API
  useEffect(() => {
    // 检查navigator.clipboard API支持
    const checkClipboardSupport = async () => {
      try {
        // 检查navigator.clipboard是否存在
        if (navigator.clipboard) {
          setClipboardSupported(true);
        } else {
          setClipboardSupported(false);
        }
      } catch (err) {
        console.warn(t('export.clipboardError', { error: err }));
        setClipboardSupported(false);
      }
    };
    
    checkClipboardSupport();
  }, [t]);
  
  // 显示通知消息
  const showToastNotification = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    
    // 3秒后自动隐藏通知
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };
  
  const handleExport = async () => {
    if (isExporting) return;
    
    setIsExporting(true);
    
    try {
      // Get the current theme from the CanvasCardRenderer component
      const currentTheme = canvasRendererRef.current?.getCurrentTheme?.();
      
      await exportCanvasImage({
        actions,
        gameInfo,
        heroInfo,
        boardCards,
        isMobile,
        theme: currentTheme,
        t
      }, `poker-hand-${new Date().getTime()}.png`);
      
      // Track image share
      trackShare('image');
      trackGameContent(gameState);
      
      showToastNotification(t('export.imageExported'));
    } catch (error) {
      console.error(t('export.exportFailed', { error: error.message }));
      showToastNotification(t('export.exportFailed', { error: error.message }));
    } finally {
      setIsExporting(false);
    }
  };
  
  // 降级复制方法 - 为不支持clipboard API的浏览器提供
  const fallbackCopyTextToClipboard = (text) => {
    // 创建一个临时的textarea元素
    const textArea = document.createElement("textarea");
    textArea.value = text;
    
    // 确保临时元素在视口外
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    
    // 选择并复制文本
    textArea.focus();
    textArea.select();
    
    let successful = false;
    try {
      successful = document.execCommand('copy');
    } catch (err) {
      console.error(t('messages.error'), err);
    }
    
    // 移除临时元素
    document.body.removeChild(textArea);
    
    if (successful) {
      setCopiedFeedback(true);
      setTimeout(() => setCopiedFeedback(false), 2000);
      showToastNotification(t('messages.copied'));
      return true;
    }
    return false;
  };
  
  // 复制文本到剪贴板功能
  const handleCopyText = async () => {
    try {
      // 尝试使用Clipboard API
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(formattedText);
        // 复制成功的视觉反馈
        setCopiedFeedback(true);
        setTimeout(() => setCopiedFeedback(false), 2000);
        
        // 跟踪文本分享
        trackShare('text');
        trackGameContent(gameState);
        
        // 显示成功通知
        showToastNotification(t('messages.copied'));
      } else {
        // 对不支持Clipboard API的浏览器使用降级方法
        const copySuccess = fallbackCopyTextToClipboard(formattedText);
        if (!copySuccess) {
          throw new Error(t('messages.error'));
        }
      }
    } catch (error) {
      console.error(t('messages.error'), error);
      showToastNotification(t('messages.error'));
    }
  };
  
  // Add a handler for copying the share link
  const handleCopyShareLink = async () => {
    try {
      const { createShareableUrl } = await import('../utils/shareUtils');
      const shareableUrl = createShareableUrl(gameState);
      
      // Try to use the clipboard API
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareableUrl);
        // Show success feedback
        setLinkCopiedFeedback(true);
        setTimeout(() => setLinkCopiedFeedback(false), 2000);
        
        // 跟踪链接分享
        trackShare('link');
        trackGameContent(gameState);
        
        showToastNotification(t('messages.copied'));
      } else {
        // Fallback for browsers that don't support the clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = shareableUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        // 跟踪链接分享
        trackShare('link');
        trackGameContent(gameState);
        
        setLinkCopiedFeedback(true);
        setTimeout(() => setLinkCopiedFeedback(false), 2000);
        showToastNotification(t('messages.copied'));
      }
    } catch (error) {
      console.error(t('messages.error'), error);
      showToastNotification(t('messages.error'));
    }
  };
  
  // 识别并合并连续相同的文本行动
  const processMergedTextActions = (lines) => {
    const result = [];
    const streetHeaders = ['Pre-flop:', 'Flop:', 'Turn:', 'River:'];
    
    let currentSection = null;
    let currentActionType = null;
    let currentAmountStr = null;
    let currentPlayers = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // 如果是分节标题，直接添加并更新当前分节
      if (line === '' || streetHeaders.some(header => line.includes(header)) || 
          line.includes('No-Limit Hold\'em') || line.includes('Final pot size:')) {
        // 处理之前收集的合并操作（如果有）
        if (currentPlayers.length > 0) {
          const mergedLine = formatMergedPlayers(currentPlayers, currentActionType, currentAmountStr);
          if (mergedLine) {
            result.push(mergedLine);
          }
          currentPlayers = [];
          currentActionType = null;
          currentAmountStr = null;
        }
        
        result.push(line);
        
        if (streetHeaders.some(header => line.includes(header))) {
          currentSection = line;
        }
        continue;
      }
      
      // 尝试解析行动信息 - 优化正则表达式以适应多种格式
      // 格式1: UTG Raise 3 (Pot: 6)
      // 格式2: ⊘ UTG+2 Fold
      const actionMatchFormat1 = line.match(/\b(Hero|UTG|UTG\+\d|LJ|HJ|CO|BTN|SB|BB)\b\s+(Fold|Call|Check|Bet|Raise|All-in)(?:\s+(\d+))?\s*(?:\(Pot:\s+(\d+)\))?/i);
      const actionMatchFormat2 = line.match(/(?:[⊘✓⟳⬆⇑★]\s+)?(Hero|UTG|UTG\+\d|LJ|HJ|CO|BTN|SB|BB)\s+(Fold|Call|Check|Bet|Raise|All-in)(?:\s+(\d+))?/i);
      
      const actionMatch = actionMatchFormat1 || actionMatchFormat2;
      
      if (actionMatch) {
        const [_, player, actionType, amount] = actionMatch;
        const amountStr = amount ? amount : '';
        
        // 输出调试信息到控制台 
        console.log(`识别到动作: 玩家=${player}, 动作=${actionType}, 金额=${amountStr}`);
        
        // 检查是否可以合并到当前组
        // 只有fold、check或相同金额的call才能被合并
        const canMerge = 
          currentActionType && currentActionType.toLowerCase() === actionType.toLowerCase() && 
          (actionType.toLowerCase() === 'fold' || actionType.toLowerCase() === 'check' || 
           (actionType.toLowerCase() === 'call' && currentAmountStr === amountStr));
        
        if (canMerge && player !== 'Hero') { // Hero的动作保持单独显示
          // 将当前行动合并到组中
          currentPlayers.push(player);
          console.log(`合并 ${player} 到当前组，当前组玩家: ${currentPlayers.join(', ')}`);
        } else {
          // 处理之前收集的合并操作（如果有）
          if (currentPlayers.length > 1) {
            const mergedLine = formatMergedPlayers(currentPlayers, currentActionType, currentAmountStr);
            if (mergedLine) {
              result.push(mergedLine);
              console.log(`创建合并行: ${mergedLine}`);
            }
          } else if (currentPlayers.length === 1) {
            // 单独一个玩家，直接添加原始行
            result.push(lines[i-1]);
          }
          
          // 开始新组或单独显示
          if (player === 'Hero' || actionType.toLowerCase() === 'bet' || 
              actionType.toLowerCase() === 'raise' || actionType.toLowerCase() === 'all-in') {
            // Hero或关键行动（下注、加注、全压）单独显示
            result.push(line);
            currentPlayers = [];
            currentActionType = null;
            currentAmountStr = null;
          } else {
            // 开始新的合并组
            currentPlayers = [player];
            currentActionType = actionType;
            currentAmountStr = amountStr;
          }
        }
        
        continue;
      }
      
      // 如果不匹配任何已知模式，直接添加
      result.push(line);
    }
    
    // 处理最后一组（如果有）
    if (currentPlayers.length > 1) {
      const mergedLine = formatMergedPlayers(currentPlayers, currentActionType, currentAmountStr);
      if (mergedLine) {
        result.push(mergedLine);
      }
    } else if (currentPlayers.length === 1) {
      // 获取最后一行的原始格式
      const lastLine = lines.find(line => line.includes(currentPlayers[0]) && 
                              (line.includes(currentActionType) || 
                               line.includes(currentActionType.toLowerCase()) || 
                               line.includes(currentActionType.toUpperCase())));
      if (lastLine) {
        result.push(lastLine);
      }
    }
    
    return result;
  };
  
  // 格式化合并后的玩家文本
  const formatMergedPlayers = (players, actionType, amountStr) => {
    if (!players || players.length <= 1) return null;
    
    // 合并多个玩家
    let playerText;
    if (players.length === 2) {
      playerText = `${players[0]}, ${players[1]}`;
    } else {
      playerText = `${players[0]}...${players[players.length - 1]}`;
    }
    
    // 构造完整的动作描述
    const amountPart = amountStr ? ` ${amountStr}` : '';
    return `${playerText} ${actionType}${amountPart}`;
  };
  
  // 格式化文本显示，增强可读性
  const renderFormattedText = () => {
    if (!formattedText) return null;
    
    // 将文本按行分割
    const lines = formattedText.split('\n');
    
    // 处理合并相同动作
    const processedLines = processMergedTextActions(lines);
    
    return (
      <div className="poker-hand-text font-sans leading-relaxed">
        {processedLines.map((line, index) => {
          // 处理游戏信息标题行（通常是第一行）
          if (index === 0 && line.includes('No-Limit Hold\'em')) {
            return (
              <div key={index} className="game-title text-white font-bold text-base md:text-lg py-3 px-4 text-center mb-2 rounded-lg shadow-sm" style={{
                background: 'linear-gradient(90deg, #2c3e50 0%, #3498db 50%, #2c3e50 100%)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
              }}>
                {line}
              </div>
            );
          }
          
          // 空行处理
          if (!line.trim()) {
            return <div key={index} className="h-2"></div>;
          }
          
          // 处理各个街道标题
          if (line.includes('Pre-flop:')) {
            return (
              <div key={index} className="street-header bg-blue-100 text-blue-900 font-semibold text-sm md:text-base py-2 px-4 border-l-4 border-blue-500 my-3">
                {line}
              </div>
            );
          }
          
          if (line.includes('Flop:')) {
            return (
              <div key={index} className="street-header bg-green-100 text-green-900 font-semibold text-sm md:text-base py-2 px-4 border-l-4 border-green-500 my-3">
                {line}
              </div>
            );
          }
          
          if (line.includes('Turn:')) {
            return (
              <div key={index} className="street-header bg-red-100 text-red-900 font-semibold text-sm md:text-base py-2 px-4 border-l-4 border-red-500 my-3">
                {line}
              </div>
            );
          }
          
          if (line.includes('River:')) {
            return (
              <div key={index} className="street-header bg-purple-100 text-purple-900 font-semibold text-sm md:text-base py-2 px-4 border-l-4 border-purple-500 my-3">
                {line}
              </div>
            );
          }

          // 处理 Showdown 标题
          if (line.includes('Showdown:')) {
            return (
              <div key={index} className="street-header bg-amber-100 text-amber-900 font-semibold text-sm md:text-base py-2 px-4 border-l-4 border-amber-500 my-3">
                {line}
              </div>
            );
          }

          // 处理玩家手牌展示信息
          if (line.match(/\b(Hero|UTG|UTG\+\d|LJ|HJ|CO|BTN|SB|BB)\b.*Shows/)) {
            const position = line.match(/\b(Hero|UTG|UTG\+\d|LJ|HJ|CO|BTN|SB|BB)\b/)[0];
            const isHero = position === 'Hero';
            
            // 提取手牌信息
            const handMatch = line.match(/Shows\s+([^\(]+)/);
            const handInfo = handMatch ? handMatch[1].trim() : '';
            
            // 提取牌力信息
            const strengthMatch = line.match(/\((.*?)\)/);
            const strengthInfo = strengthMatch ? strengthMatch[1] : '';
            
            return (
              <div key={index} 
                className={`showdown-line ${isHero ? 'bg-yellow-50 border-l-4 border-yellow-400' : 'bg-indigo-50'} 
                text-gray-800 text-sm my-1 py-2 px-4 rounded-r-lg transition-all hover:shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between`}>
                <span className="font-medium">
                  <span className="font-semibold">{position}</span> Shows <span className="text-indigo-700 font-semibold">{handInfo}</span>
                </span>
                {strengthInfo && (
                  <span className="text-gray-600 text-xs sm:text-sm mt-1 sm:mt-0">
                    {strengthInfo}
                  </span>
                )}
              </div>
            );
          }

          // 处理赢家信息
          if (line.includes('collected') && line.includes('from pot')) {
            const position = line.match(/\b(Hero|UTG|UTG\+\d|LJ|HJ|CO|BTN|SB|BB)\b/)[0];
            const amount = line.match(/collected\s+(\d+)/)[1];
            
            return (
              <div key={index} className="winner-line bg-green-50 text-green-800 font-medium text-sm py-2 px-4 rounded-r-lg my-1 border-l-4 border-green-400">
                <span className="font-semibold">{position}</span> wins <span className="font-bold">{amount}</span> chips
              </div>
            );
          }
          
          // 检查是否是合并后的行
          if (line.includes('(合并')) {
            return (
              <div key={index} className="action-line bg-yellow-50 text-yellow-800 font-medium text-sm py-1.5 px-4 rounded-r-lg my-1">
                {line}
              </div>
            );
          }
          
          // 处理玩家操作行
          if (line.match(/\b(Hero|UTG|UTG\+\d|LJ|HJ|CO|BTN|SB|BB)\b/)) {
            // 分割位置和动作
            const position = line.match(/\b(Hero|UTG|UTG\+\d|LJ|HJ|CO|BTN|SB|BB)\b/)[0];
            
            // 着色不同的动作
            let actionClass = '';
            let bgColorClass = '';
            
            if (line.includes('Fold')) {
              actionClass = 'text-gray-700';
              bgColorClass = 'bg-gray-100';
            } else if (line.includes('Call')) {
              actionClass = 'text-blue-700';
              bgColorClass = 'bg-blue-50';
            } else if (line.includes('Check')) {
              actionClass = 'text-green-700';
              bgColorClass = 'bg-green-50';
            } else if (line.includes('Bet')) {
              actionClass = 'text-amber-700';
              bgColorClass = 'bg-amber-50';
            } else if (line.includes('Raise')) {
              actionClass = 'text-red-700';
              bgColorClass = 'bg-red-50';
            } else if (line.includes('All-in')) {
              actionClass = 'text-purple-800';
              bgColorClass = 'bg-purple-50';
            }
            
            // 如果是Hero的操作，给予特殊强调
            const isHero = position === 'Hero';
            
            // 去除行首可能存在的图标
            const cleanLine = line.replace(/^[⊘✓⟳⬆⇑★]\s+/, '');
            
            // 找出并高亮位置标识
            const formattedLine = cleanLine.replace(/(Hero|UTG|UTG\+\d|LJ|HJ|CO|BTN|SB|BB)/, '<span class="font-semibold">$1</span>');
            
            return (
              <div key={index} className={`action-line ${bgColorClass} ${actionClass} ${isHero ? 'border-l-4 border-yellow-400' : ''} text-sm my-1 py-1.5 px-4 rounded-r-lg transition-all hover:shadow-sm`}>
                <span className={`${isHero ? 'font-semibold' : ''}`} dangerouslySetInnerHTML={{ __html: formattedLine }}></span>
              </div>
            );
          }
          
          // 处理最终底池大小
          if (line.includes('Final pot size:')) {
            return (
              <div key={index} className="final-pot bg-green-700 text-white font-bold text-base py-3 px-4 rounded-b-lg text-center mt-4">
                {line}
              </div>
            );
          }
          
          // 默认行样式
          return <div key={index} className="px-4 py-1 text-sm">{line}</div>;
        })}
      </div>
    );
  };
  
  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* 按钮组 */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex flex-row gap-0.5 sm:gap-1">
          {/* 图片/文本模式切换按钮 */}
          <button
            onClick={() => setDisplayMode(displayMode === 'text' ? 'image' : 'text')}
            className="flex-1 flex items-center justify-center py-1 sm:py-2 px-0.5 sm:px-2 text-center bg-white text-gray-700 text-[10px] sm:text-base"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-5 sm:w-5 mr-0.5 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="font-medium whitespace-nowrap">
              {displayMode === 'text' ? t('export.imageMode') : t('export.textMode')}
            </span>
          </button>

          {/* 复制文本按钮 */}
          <button
            onClick={handleCopyText}
            className="flex-1 flex items-center justify-center py-1 sm:py-2 px-0.5 sm:px-2 text-center bg-white text-gray-700 text-[10px] sm:text-base"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-5 sm:w-5 mr-0.5 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="font-medium whitespace-nowrap">{t('export.copyText')}</span>
          </button>

          {/* 分享链接按钮 */}
          <button
            onClick={handleCopyShareLink}
            className="flex-1 flex items-center justify-center py-1 sm:py-2 px-0.5 sm:px-2 text-center bg-white text-gray-700 text-[10px] sm:text-base"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-5 sm:w-5 mr-0.5 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span className="font-medium whitespace-nowrap">{t('export.shareLink')}</span>
          </button>

          {/* 下载图片按钮 */}
          <button
            onClick={handleExport}
            disabled={isExporting}
            className={`flex-1 flex items-center justify-center py-1 sm:py-2 px-0.5 sm:px-2 text-center bg-white text-gray-700 text-[10px] sm:text-base ${
              isExporting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-5 sm:w-5 mr-0.5 sm:mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span className="font-medium whitespace-nowrap">{t('export.downloadImage')}</span>
          </button>
        </div>
      </div>

      {/* 预览区域 */}
      <div className="bg-white rounded-lg overflow-hidden shadow-sm">
        {/* 预览内容 */}
        <div className="preview-content">
          {displayMode === 'text' ? (
            <div className="text-preview p-0 max-h-[450px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {renderFormattedText()}
            </div>
          ) : (
            <CanvasCardRenderer
              ref={canvasRendererRef}
              actions={actions}
              gameInfo={gameInfo}
              heroInfo={heroInfo}
              boardCards={boardCards}
              isMobile={isMobile}
              className="scale-preview"
              t={t}
            />
          )}
        </div>
      </div>

      {/* 通知横幅 */}
      {showNotification && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg z-50 font-medium">
          {notificationMessage}
        </div>
      )}
    </div>
  );
};

export default ExportSelector; 